import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { officialRequestsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
    Mail, MailPlus, MailCheck, Paperclip, ChevronLeft, Loader2,
    AlertCircle, Clock, CheckCircle2, Send, ArrowRight, Inbox,
} from 'lucide-react';
import NewOfficialRequestDialog from '@/components/official-requests/NewOfficialRequestDialog';
import OfficialRequestDetail from '@/components/official-requests/OfficialRequestDetail';

const DIRIGEANT_EMAILS = ['jeremy@valnexo.fr', 'morgan@valnexo.fr', 'laurent@valnexo.fr'];

const STATUS_META = {
    nouvelle: { label: 'Nouvelle', className: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertCircle },
    en_cours: { label: 'En cours', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
    traitee: { label: 'Traitée', className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
};

export default function DemandesOfficiellesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { id: routeReqId } = useParams();

    const email = (user?.email || '').toLowerCase();
    const isDirigeant = DIRIGEANT_EMAILS.includes(email);
    // Vue "envoi" (VP + admins) OU vue "réception" (dirigeants). Dirigeant peut basculer sur sa vue d'envoi si besoin :
    const [inboxMode, setInboxMode] = useState(isDirigeant);

    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [selectedId, setSelectedId] = useState(routeReqId || null);
    // Secteur pré-sélectionné (venant de l'organigramme via query string)
    const [prefillArea, setPrefillArea] = useState(null);

    // Lire ?area=... depuis l'URL pour pré-remplir le formulaire
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const area = params.get('area');
        if (area) {
            setPrefillArea(area);
            setShowNew(true);
            // nettoyer l'URL
            window.history.replaceState({}, '', '/demandes-officielles');
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await officialRequestsAPI.list();
            setItems(res.data?.items || []);
            setUnread(res.data?.unread || 0);
        } catch (e) {
            toast.error("Impossible de charger les demandes officielles");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleCreated = () => {
        setShowNew(false);
        setPrefillArea(null);
        load();
        toast.success("Demande officielle envoyée !");
    };

    const handleUpdated = () => {
        load();
    };

    const selectedItem = items.find(i => i.id === selectedId) || null;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="demandes-officielles-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
                            <Mail className="w-7 h-7 text-indigo-600" />
                            Demandes officielles
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {isDirigeant && inboxMode
                                ? "Boîte de réception — les demandes officielles reçues des responsables (VP)."
                                : "Envoyez une demande officielle à un dirigeant selon le secteur concerné."}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isDirigeant && (
                            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="demandes-mode-toggle">
                                <button
                                    type="button"
                                    onClick={() => { setInboxMode(true); setSelectedId(null); load(); }}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 ${inboxMode ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                    data-testid="demandes-mode-inbox"
                                >
                                    <Inbox className="w-3.5 h-3.5" />
                                    Reçues
                                    {inboxMode && unread > 0 && (
                                        <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded-full">{unread}</span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setInboxMode(false); setSelectedId(null); load(); }}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 ${!inboxMode ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                    data-testid="demandes-mode-sent"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                    Envoyées
                                </button>
                            </div>
                        )}
                        {!inboxMode && (
                            <Button
                                onClick={() => { setPrefillArea(null); setShowNew(true); }}
                                data-testid="new-official-request-btn"
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                                <MailPlus className="w-4 h-4 mr-1.5" />
                                Nouvelle demande
                            </Button>
                        )}
                    </div>
                </div>

                {/* Contenu — soit détail, soit liste */}
                {selectedItem ? (
                    <OfficialRequestDetail
                        item={selectedItem}
                        currentUser={user}
                        isDirigeantView={isDirigeant && inboxMode}
                        onBack={() => setSelectedId(null)}
                        onUpdated={handleUpdated}
                    />
                ) : (
                    <RequestsList
                        loading={loading}
                        items={items}
                        inbox={isDirigeant && inboxMode}
                        onSelect={setSelectedId}
                        onCreate={() => { setPrefillArea(null); setShowNew(true); }}
                    />
                )}
            </div>

            {/* Modal de création */}
            <NewOfficialRequestDialog
                open={showNew}
                prefillArea={prefillArea}
                onOpenChange={(v) => { setShowNew(v); if (!v) setPrefillArea(null); }}
                onCreated={handleCreated}
            />
        </DashboardLayout>
    );
}

function RequestsList({ loading, items, inbox, onSelect, onCreate }) {
    if (loading) {
        return (
            <Card>
                <CardContent className="py-16 text-center text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Chargement des demandes…
                </CardContent>
            </Card>
        );
    }
    if (!items.length) {
        return (
            <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="py-16 text-center">
                    <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">
                        {inbox ? "Aucune demande officielle reçue." : "Vous n'avez encore envoyé aucune demande."}
                    </p>
                    {!inbox && (
                        <Button variant="outline" onClick={onCreate} className="mt-4" data-testid="new-official-request-empty-btn">
                            <MailPlus className="w-4 h-4 mr-1.5" />
                            Créer ma première demande
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }
    return (
        <div className="space-y-3">
            {items.map((it) => {
                const meta = STATUS_META[it.status] || STATUS_META.nouvelle;
                const StatusIcon = meta.icon;
                const isUnreadReply = !inbox && it.status === 'traitee' && !it.read_by_sender_at;
                const isUnreadIncoming = inbox && it.status === 'nouvelle';
                const attCount = (it.attachments || []).length;
                return (
                    <button
                        key={it.id}
                        onClick={() => onSelect(it.id)}
                        data-testid={`official-request-item-${it.id}`}
                        className={`w-full text-left rounded-xl border bg-white hover:shadow-md transition-all px-4 py-3 flex items-start gap-3 ${(isUnreadReply || isUnreadIncoming) ? 'border-indigo-300 ring-1 ring-indigo-100' : 'border-slate-200'}`}
                    >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${it.status === 'traitee' ? 'bg-emerald-100' : it.status === 'en_cours' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            {it.status === 'traitee' ? <MailCheck className="w-5 h-5 text-emerald-700" />
                                : <Mail className={`w-5 h-5 ${it.status === 'en_cours' ? 'text-blue-700' : 'text-amber-700'}`} />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className={`text-[11px] ${meta.className}`}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {meta.label}
                                </Badge>
                                <span className="text-xs text-slate-500">{it.subject_label}</span>
                                {(isUnreadReply || isUnreadIncoming) && (
                                    <span className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">
                                        {isUnreadIncoming ? 'À traiter' : 'Réponse reçue'}
                                    </span>
                                )}
                            </div>
                            <div className="mt-0.5 font-semibold text-slate-900 truncate">{it.title}</div>
                            <div className="mt-0.5 text-xs text-slate-500 truncate">
                                {inbox
                                    ? <>De <strong className="text-slate-700">{it.sender_name}</strong></>
                                    : <>À <strong className="text-slate-700">{(it.recipient_names || []).join(', ')}</strong></>}
                                {' · '}
                                {new Date(it.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                {attCount > 0 && (
                                    <span className="ml-2 inline-flex items-center gap-1 text-slate-500">
                                        <Paperclip className="w-3 h-3" />{attCount}
                                    </span>
                                )}
                            </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                    </button>
                );
            })}
        </div>
    );
}
